// Typed coordinate entry: turn what a person pastes from a survey plan, an
// email or a chart into a lat/lon, or refuse.
//
// Accepted spellings, in one parser rather than a mode switch the user has to
// know about:
//
//   27.5, -94.2            decimal, signed (comma optional)
//   27.5 N 94.2 W          decimal with hemisphere letters (suffix or prefix)
//   27°30'N 94°12'W        degrees–minutes, with or without the marks
//   27 30 15 N 94 12 30 W  degrees–minutes–seconds
//
// Order is free when hemispheres are named ("94.2 W 27.5 N" works); bare
// numbers are only ever read as one signed decimal pair, because "27 30 94 12"
// without letters could be two DM pairs or four of anything — ambiguity is
// rejected, not guessed at. Out-of-range values (lat past ±90, lon past ±180,
// minutes or seconds ≥ 60) are refusals too: a typo that parses is worse than
// one that doesn't, since the console would then fly somewhere plausible.

export interface ParsedCoords {
  lat: number;
  lon: number;
}

const NUM = /-?\d+(?:\.\d+)?/g;

interface Half {
  axis: "lat" | "lon" | null; // named by a hemisphere letter, or null
  value: number;
}

/** One half of an entry — everything describing a single angle. */
function parseHalf(text: string): Half | null {
  const letters = text.match(/[NSEW]/g) ?? [];
  if (letters.length > 1) return null;
  const hemi = letters.length === 1 ? letters[0] : null;

  const nums = text.match(NUM) ?? [];
  if (nums.length < 1 || nums.length > 3) return null;

  // Whatever is neither a number nor the hemisphere must be pure separator —
  // "27.5X" or "about 27" is garbage, not a coordinate.
  const residue = text.replace(NUM, " ").replace(/[NSEW]/g, " ").trim();
  if (residue !== "") return null;

  const parts = nums.map(Number);
  const deg = parts[0];
  const min = parts[1] ?? 0;
  const sec = parts[2] ?? 0;
  if (parts.some((p) => !Number.isFinite(p))) return null;
  // In DMS the sign belongs to the hemisphere (or to the degrees alone when no
  // letter is given); negative minutes or seconds are always a mistake.
  if (min < 0 || min >= 60 || sec < 0 || sec >= 60) return null;
  if (hemi !== null && deg < 0) return null; // "-27.5 N" contradicts itself
  if (parts.length > 1 && !Number.isInteger(deg) && (min !== 0 || sec !== 0)) return null; // "27.5 30" mixes forms

  const magnitude = Math.abs(deg) + min / 60 + sec / 3600;
  const sign = hemi === "S" || hemi === "W" || (hemi === null && deg < 0) ? -1 : 1;
  const axis = hemi === null ? null : hemi === "N" || hemi === "S" ? "lat" : "lon";
  return { axis, value: sign * magnitude };
}

/** Parse a typed position. Null means "can't read that" — the caller shows a
 *  quiet error and leaves the picked point alone. */
export function parseCoords(raw: string): ParsedCoords | null {
  let s = raw.trim().toUpperCase();
  if (s === "") return null;
  // Degree/minute/second marks (typed or pasted, straight or curly) only ever
  // separate numbers here, so they normalise to spaces before splitting.
  s = s.replace(/[°º′’'″”"]/g, " ");

  let a: string;
  let b: string;
  if (s.includes(",")) {
    const halves = s.split(",");
    if (halves.length !== 2) return null;
    [a, b] = halves as [string, string];
  } else {
    const letterAt: number[] = [];
    for (let i = 0; i < s.length; i++) {
      if ("NSEW".includes(s[i])) letterAt.push(i);
    }
    if (letterAt.length === 2) {
      // Suffix style ("27.5 N 94.2 W") splits after the first letter; prefix
      // style ("N27.5 W94.2") before the second.
      const suffixStyle = /\d/.test(s.slice(0, letterAt[0]));
      const cut = suffixStyle ? letterAt[0] + 1 : letterAt[1];
      a = s.slice(0, cut);
      b = s.slice(cut);
    } else if (letterAt.length === 0) {
      // No letters, no comma: only a plain signed pair is unambiguous.
      const matches = [...s.matchAll(NUM)];
      if (matches.length !== 2 || matches[1].index === undefined) return null;
      a = s.slice(0, matches[1].index);
      b = s.slice(matches[1].index);
    } else {
      return null;
    }
  }

  const ha = parseHalf(a);
  const hb = parseHalf(b);
  if (ha === null || hb === null) return null;

  let lat: number;
  let lon: number;
  if (ha.axis !== null && hb.axis !== null) {
    if (ha.axis === hb.axis) return null; // "27 N 30 N" names no longitude
    lat = ha.axis === "lat" ? ha.value : hb.value;
    lon = ha.axis === "lon" ? ha.value : hb.value;
  } else if (ha.axis === "lat" || hb.axis === "lon") {
    lat = ha.value;
    lon = hb.value;
  } else if (ha.axis === "lon" || hb.axis === "lat") {
    lon = ha.value;
    lat = hb.value;
  } else {
    // Neither named: latitude first, the convention everywhere at sea.
    lat = ha.value;
    lon = hb.value;
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}
